import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Button Component - Prozpector CRM Design System
 *
 * Variants:
 * - default: Primary actions with Purple (Stripe-inspired)
 * - outline: Tertiary actions with border
 * - ghost: Minimal buttons
 * - destructive (Red): Dangerous actions
 *
 * All buttons use Purple by default with smooth hover animations
 */

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transform hover:scale-105 active:scale-95 max-w-md",
  {
    variants: {
      variant: {
        // Default: Purple - ALL buttons use primary color
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 focus-visible:ring-primary shadow-md hover:shadow-xl",

        // Primary: Purple - Alias for default
        primary: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 focus-visible:ring-primary shadow-md hover:shadow-xl",

        // Outline: Purple border with primary text (consistent with design system)
        outline: "border-2 border-primary/30 bg-transparent text-primary hover:bg-primary/10 hover:border-primary/50 active:bg-primary/20 focus-visible:ring-primary/50",

        // Ghost: Minimal buttons with Purple hover
        ghost: "text-gray-700 hover:bg-primary/10 hover:text-primary active:bg-primary/20 focus-visible:ring-primary",

        // Destructive: Red (Dangerous actions only)
        destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-600 shadow-md hover:shadow-xl",

        // Secondary: Gray/neutral variant
        secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 active:bg-gray-300 focus-visible:ring-gray-500 shadow-sm hover:shadow-md",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        default: "h-10 px-4 py-2",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default", // All buttons are purple by default
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
